import React, { ReactNode, useEffect, useState } from 'react'

import { getUserUsageInnerRoute } from '../api/auth'
import LimitIcon from '../assets/icons/limit.svg'
import { Chord, Instrument, InstrumentLayer, ProgPart, TFrontendLimitType, TLimitType } from '../utils/types'
import { useCommonModals } from './CommonModalsContext'
import { useCommonModalsLight } from './CommonModalsLightContext'
import { useCurrentUser } from './CurrentUserContext'

export enum FRONTEND_LIMIT_TYPES {
  song_sections = 'song_sections',
  song_track_layers = 'song_track_layers',
  song_section_chords = 'song_section_chords',
  drums_autocomplete = 'drums_autocomplete',
  chord_palette = 'chord_palette',
}

export type TFrontendLimit = keyof typeof FRONTEND_LIMIT_TYPES
export type TFrontendLimits = { [key in TFrontendLimit]: number }
export type TFrontendModes = { anonymous: TFrontendLimits; free: TFrontendLimits; paid: TFrontendLimits }

type FetchType = {
  isPartsLimited: (parts: ProgPart[]) => boolean
  isLayersLimited: (layers: InstrumentLayer[]) => boolean
  isChordsLimited: (oldChords: Chord[], newChords?: Chord[]) => boolean
  isDrumsAutocompleteLimited: (autocomplete: number) => boolean
  isChordEditorLimited: () => boolean
  isInstrumentLimited: (instrument: Instrument) => boolean

  renderLimitIcon: (isLimited: boolean, className?: string) => ReactNode
  triggerLimitCallback: (isLimited?: boolean, limit?: TLimitType | TFrontendLimitType) => void
}

export const LimitsContext = React.createContext<FetchType>({
  isPartsLimited: () => false,
  isLayersLimited: () => false,
  isChordsLimited: () => false,
  isDrumsAutocompleteLimited: () => false,
  isChordEditorLimited: () => false,
  isInstrumentLimited: () => false,

  renderLimitIcon: () => <></>,
  triggerLimitCallback: () => {},
})

const limitIconStyles = {
  position: 'absolute',
  right: 10,
  top: 10,

  width: 20,
  height: 20,

  color: '#F4F4F8',
}

export const LimitsProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useCurrentUser()
  const { setLoginOpen, setLoginLimitType } = useCommonModalsLight()
  const { handleOpenPricing } = useCommonModals()

  const [limits, setLimits] = useState<{ [key in TLimitType]: number } | null>(null)

  useEffect(() => {
    getUserUsageInnerRoute().then((data) => {
      const newLimits = Object.fromEntries(
        Object.entries(data).map(([limitKey, limitData]: any) => [limitKey, limitData.limit]),
      )
      setLimits(newLimits)
    })
  }, [])

  const checkIfLimited = (currentUsage: number, expectedUsage: number, limit: number) => {
    return limit !== -1 && (currentUsage >= limit || expectedUsage > limit)
  }

  const isPartsLimited = (parts: ProgPart[]) => {
    if (!limits) return false
    return checkIfLimited(parts.length, 0, limits.song_sections)
  }
  const isLayersLimited = (layers: InstrumentLayer[]) => {
    if (!limits) return false
    return checkIfLimited(layers.length, 0, limits.song_track_layers)
  }
  const isChordsLimited = (oldChords: Chord[], newChords?: Chord[]) => {
    if (!limits) return false
    return checkIfLimited(oldChords.length, newChords?.length || 0, limits.song_section_chords)
  }
  const isDrumsAutocompleteLimited = (autocomplete: number) => {
    if (!limits) return false
    return checkIfLimited(autocomplete, 0, limits.chord_palette)
  }
  const isChordEditorLimited = () => !currentUser?.isSubscribed
  const isInstrumentLimited = (instrument: Instrument) => {
    return instrument.premium && !currentUser?.isSubscribed
  }

  const renderLimitIcon = (isLimited: boolean, className?: string) => {
    if (!isLimited) return null
    return <LimitIcon style={limitIconStyles} className={className} />
  }
  const triggerLimitCallback = (isLimited?: boolean, limit?: TLimitType | TFrontendLimitType) => {
    if (isLimited === false) return

    if (!currentUser) {
      setLoginOpen(true)
      setLoginLimitType(limit || null)

      return
    }

    handleOpenPricing(true, limit)
  }

  return (
    <LimitsContext.Provider
      value={{
        isPartsLimited,
        isLayersLimited,
        isChordsLimited,
        isDrumsAutocompleteLimited,
        isChordEditorLimited,
        isInstrumentLimited,

        renderLimitIcon,
        triggerLimitCallback,
      }}
    >
      {children}
    </LimitsContext.Provider>
  )
}

export const useLimits = () => React.useContext<FetchType>(LimitsContext)
